import { ColumnDefinition } from 'components/TableColumn/TableColumn';
import moment from 'moment';
import { OrdersWithPagination } from 'ws/OrderWebService';
import { OrderManager, DefaultOrderManager } from 'core/order/OrderManager';
import {
  UpdateEventListener,
  FireableUpdateEventListener,
} from 'utils/UpdateEventListener';
import {
  State,
  Order,
  OrderDelivery,
  OrderCampaignStatus,
} from 'core/order/Order';
import i18n from 'i18next';
import { toast } from 'react-toastify';

import { formatPrice } from 'helper/CurrencyHelper';
import { numberWithCommas } from 'utils/StringUtil';
import styles from './OrderList.module.scss';
import _ from 'lodash';
import { AddonFeatureManager } from 'core';
import {
  SortableList,
  AbstractSortableList,
} from 'containers/Common/AbstractSortableList';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';
import { SelectOptions } from 'components/commonType';
import { FilterMenuTabConfig } from 'components/FilterMenuTab/FilterMenuTab';

export interface OrderListModel extends SortableList {
  readonly event: UpdateEventListener<OrderListModel>;
  readonly state: OrderListState;
  readonly noDataDescription: string;
  readonly filterMenuTabConfigs: Array<FilterMenuTabConfig>;
  readonly ordersSizePerPage: number;
  readonly searchString: string;
  readonly orderDeliveriesOptions: Array<SelectOptions>;
  readonly orderDeliveries: Array<string>;
  readonly orderTags: Array<string>;
  init(page?: number): Promise<void>;
  changeAdvertiser(advertiserId): Promise<void>;
  getViewModelData(): any;
  setAdvertisers(advertisers): void;
  getColumnDefinition(columnName, sort?): ColumnDefinition;
  handleOnSearch(searchString: string): void;
  handleSelect(order: Order): void;
  handleSelectAll(): void;
  handleRemoveSelect(): void;
  bulkActivateOrders(): void;
  bulkDeactivateOrders(): void;
  bulkApproveOrders(): void;
  bulkRejectOrders(): void;
  setSearchbarRef(ref): void;
  handleOnStateFilterApply(stateDesList): void;
  handleCreatorInfoApply(orderIds: number[]): void;
  sortOrders(field, direction): void;
  getReportPath(order): string;
  getReportRangeNote(order): string;
  onUnmount(handler): void;
}

export enum OrderListColumns {
  NAME = 'orderNumber',
  ADVERTISER = 'advertiser',
  APPROVAL = 'state',
  DAYRANGE = 'dayRange',
  REMAINDAY = 'remainingDay',
  PROGRESS = 'progress',
  IMPRES = 'impres',
  CLICKS = 'clicks',
  CTR = 'ctr',
  ROAS = 'roas',
  EDITBTNS = 'editBtns',
}

export type OrderListProps = {
  readonly model: OrderListModel;
};

export type OrderListState = {
  readonly ordersWithPagination?: OrdersWithPagination;
  readonly loading: boolean;
  readonly redirectPath?: string;
  readonly selectedStateFilter: string[];
  readonly selectedOrdersToShowInfo: number[];
  readonly selectedOrder: number[];
};

export class DefaultOrderListModel
  extends AbstractSortableList
  implements OrderListModel
{
  loading: boolean;
  manager: OrderManager;
  ordersWithPagination?: OrdersWithPagination;
  event: FireableUpdateEventListener<OrderListModel>;
  ordersSizePerPage: number;
  advertiserId?: number;
  advertisers: any;
  orderDeliveries: string[] = [];
  orderDeliveriesOptions: SelectOptions[] = [];
  orderTags: string[] = [];
  searchString: string;
  searchbarRef: any;
  selectedStateFilter: string[] = [];
  selectedOrdersToShowInfo: number[] = [];
  selectedOrder: number[] = [];
  debouncedInit: any;
  redirectPath?: string;
  addonFeatureManager: AddonFeatureManager;

  constructor(
    advertiserId: number | undefined,
    addonFeatureManager: AddonFeatureManager,
    manager: OrderManager = new DefaultOrderManager(),
  ) {
    super(OrderListColumns.REMAINDAY, 'desc');
    this.manager = manager;
    this.advertiserId = advertiserId;
    this.addonFeatureManager = addonFeatureManager;
    this.loading = false;
    this.event = new FireableUpdateEventListener<OrderListModel>();
    this.ordersSizePerPage = 10;
    this.searchString = '';
    this.debouncedInit = _.debounce(this.init.bind(this), 1000);
  }

  async init(page: number | undefined = 1): Promise<void> {
    this.updateState(true);
    try {
      const sortMap = {
        [OrderListColumns.DAYRANGE]: 'startDate',
        [OrderListColumns.PROGRESS]: 'adMetrics.spent',
        [OrderListColumns.IMPRES]: 'adMetrics.impres',
        [OrderListColumns.CLICKS]: 'adMetrics.clicks',
        [OrderListColumns.CTR]: 'adMetrics.ctr',
        [OrderListColumns.ROAS]: 'adMetrics.roas',
      };

      const sort = sortMap[this.sortField] || this.sortField;

      const deliveries = this.orderDeliveriesOptions.filter((selectOption) =>
        this.selectedStateFilter.includes(selectOption.label),
      );
      const pageable = {
        page,
        sizePerPage: this.ordersSizePerPage,
        sort,
        direction: this.sortOrder,
      };
      this.ordersWithPagination = await this.manager.getOrders(
        this.advertiserId,
        pageable,
        this.searchString,
        deliveries.map((option) => option.value.toString()),
      );
      this.selectedOrdersToShowInfo = [];
      this.updateState(false);
    } catch (error) {
      this.updateState(false);
    }
  }

  setAdvertisers(advertisers) {
    this.advertisers = advertisers;
  }

  setSearchbarRef(ref) {
    this.searchbarRef = ref;
  }

  handleOnSearch = (searchString: string): void => {
    this.searchString = searchString;
    if (this.searchString === '') {
      this.debouncedInit && this.debouncedInit.cancel();
      this.init();
    } else {
      this.debouncedInit();
    }
  };

  handleOnStateFilterApply = (stateDesList: any): void => {
    this.selectedStateFilter = [...stateDesList];
    this.init();
  };

  handleCreatorInfoApply = (orderIds: number[]): void => {
    this.selectedOrdersToShowInfo = [...orderIds];
    this.event.fireEvent(this);
  };

  getReportPath(order) {
    const startMoment = moment(order.dayRange.startDate).startOf('day');
    let endMoment = moment(order.dayRange.endDate).endOf('day');
    // 限制報表查詢範圍最大為 92 天
    if (endMoment.diff(startMoment, 'days') > 92) {
      endMoment = startMoment.clone().add(92, 'days').endOf('day');
    }
    const from = encodeURIComponent(startMoment.format('YYYY-MM-DD HH:mm:ss'));
    const to = encodeURIComponent(endMoment.format('YYYY-MM-DD HH:mm:ss'));
    return `/reports/performance?dimension=adsOrderId&from=${from}&to=${to}&adsOrderId=${order.id}`;
  }

  getReportRangeNote(order): string {
    const startMoment = moment(order.dayRange.startDate).startOf('day');
    let endMoment = moment(order.dayRange.endDate).endOf('day');
    // 限制報表查詢範圍最大為 92 天
    if (endMoment.diff(startMoment, 'days') > 92) {
      return '( 僅顯示 92 天 )';
    }
    return '';
  }

  getRemainDayDesc(order) {
    let today = moment().startOf('day');
    let startDate = moment(order.startDate, 'YYYY-MM-DD');
    let endDate = moment(order.endDate, 'YYYY-MM-DD');
    let diffStart = today.diff(startDate, 'days');
    let diffEnd = endDate.diff(today, 'days');
    if (diffStart < 0) {
      return i18n.t<string>('orderDetail.labels.notstart');
    } else {
      if (diffEnd < 0) {
        return i18n.t<string>('orderDetail.labels.finished');
      } else if (diffEnd === 0) {
        return i18n.t<string>('orderDetail.labels.today');
      } else {
        return diffEnd + 1;
      }
    }
  }

  getSpentStatus = (actualProgress, startDate, endDate) => {
    const getPredictRate = (startDate, endDate) => {
      const startTime = moment(startDate).startOf('day');
      const endTime = moment(endDate).startOf('day');
      const now = moment().startOf('day');

      if (moment().isBefore(moment(startDate))) {
        return 0;
      } else if (moment().isAfter(moment(endDate))) {
        return 1;
      } else {
        return (
          (now.diff(startTime, 'day') + 1) /
          (endTime.diff(startTime, 'day') + 1)
        );
      }
    };
    const predictRate = getPredictRate(startDate, endDate);
    const discrepancy = predictRate - actualProgress;

    return {
      executeRate: actualProgress,
      danger: actualProgress > 1.1,
      deepWarning: discrepancy > 0.03,
      warning: discrepancy > 0.01 && discrepancy <= 0.03,
    };
  };

  getTags = (campaignStatus) => {
    const allTagList = [
      {
        tag: OrderCampaignStatus.AHEAD,
        name: i18n.t<string>('orderList.labels.delivery.ahead'),
      },
      {
        tag: OrderCampaignStatus.SEVERELY_BEHIND,
        name: i18n.t<string>('orderList.labels.delivery.severelyBehind'),
      },
      {
        tag: OrderCampaignStatus.BEHIND,
        name: i18n.t<string>('orderList.labels.delivery.behind'),
      },
      {
        tag: OrderCampaignStatus.NO_CREATIVES,
        name: i18n.t<string>('orderList.labels.delivery.noCreatives'),
      },
      {
        tag: OrderCampaignStatus.NOT_START,
        name: i18n.t<string>('orderList.labels.delivery.notStart'),
      },
      {
        tag: OrderCampaignStatus.NORMAL,
        name: i18n.t<string>('orderList.labels.delivery.normal'),
      },
      {
        tag: OrderCampaignStatus.INACTIVE,
        name: i18n.t<string>('orderList.labels.delivery.inactive'),
      },
      {
        tag: OrderCampaignStatus.END,
        name: i18n.t<string>('orderList.labels.delivery.end'),
      },
    ];

    return allTagList
      .map(({ tag, name }) => ({
        tag,
        value: campaignStatus[tag],
        label: `${name} ${campaignStatus[tag]}`,
      }))
      .filter(({ value }) => value > 0);
  };

  getViewModelData() {
    this.orderDeliveriesOptions = createSelectOptionsFromEnum(
      OrderDelivery,
      'orderDetail.labels.',
    );
    this.orderDeliveries = [];
    this.orderTags = [];
    if (!this.ordersWithPagination) {
      return [];
    }
    const viewModelData: Array<any> = this.ordersWithPagination.orders.map(
      (order) => {
        this.orderDeliveries = this.orderDeliveriesOptions.map(
          (selectOption) => selectOption.label,
        );
        return {
          id: order.id,
          needAlert: order.needAlert,

          orderNumber: {
            value: order.orderNumber,
            name: order.projectName,
            orderNumber: `ID: ${order.orderNumber}`,
          },
          advertiser: this.advertisers
            ? this.advertisers[order.advertiserId]
            : '',
          state: {
            value: order.state,
            desc: i18n.t<string>(
              `order.status.${State[order.state].toLowerCase().replace(/_/g, '')}`,
            ),
          },
          remainingDay: this.getRemainDayDesc(order),
          [OrderListColumns.DAYRANGE]: {
            startDate: order.startDate,
            endDate: order.endDate,
          },
          [OrderListColumns.PROGRESS]: {
            spent: formatPrice(
              order.currency,
              order.rtbCampaignRunningStatus.spent,
            ),
            budget: formatPrice(
              order.currency,
              order.rtbCampaignRunningStatus.budget,
            ),
            spentStatus: this.getSpentStatus(
              order.rtbCampaignRunningStatus.actualProgress,
              order.startDate,
              order.endDate,
            ),
            tags: this.getTags(order.rtbCampaignRunningStatus),
          },
          [OrderListColumns.IMPRES]: numberWithCommas(order?.impres || 0),
          [OrderListColumns.CLICKS]: numberWithCommas(order?.clicks || 0),
          [OrderListColumns.CTR]: `${((order?.ctr || 0) * 100).toFixed(2)}%`,
          [OrderListColumns.ROAS]: numberWithCommas(
            (order?.roas || 0).toFixed(2),
          ),
          creator: order.creator,
          creatorEmail: order.creatorEmail,
          createDateTime: order.createDateTime,
        };
      },
    );
    return viewModelData;
  }

  getColumnDefinitionAddition(columnName) {
    switch (columnName) {
      case OrderListColumns.ADVERTISER:
        return {
          sort: false,
        };
      case OrderListColumns.EDITBTNS:
        return {
          text: '',
          sort: false,
        };
      default:
        return {};
    }
  }

  getColumnDefinition(columnName, sort = true): ColumnDefinition {
    const columnClassGetter = () => {
      return styles[columnName];
    };

    return {
      sort,
      text: `orderList.headers.${columnName}`,
      dataField: columnName,
      classes: columnClassGetter,
      headerClasses: columnClassGetter,
      ...this.getColumnDefinitionAddition(columnName),
    };
  }

  async changeAdvertiser(advertiserId): Promise<void> {
    this.advertiserId = advertiserId;
    if (this.searchbarRef) {
      this.searchbarRef.current.clear();
    } else {
      await this.init();
    }
  }

  handleSelect = (orderId) => {
    if (this.selectedOrder.indexOf(orderId) > -1) {
      _.remove(this.selectedOrder, (id) => id === orderId);
    } else {
      this.selectedOrder.push(orderId);
    }
    this.updateState(false);
  };

  handleSelectAll = () => {
    const orders = this.ordersWithPagination?.orders || [];
    if (this.selectedOrder.length === orders.length) {
      this.selectedOrder = [];
    } else {
      this.selectedOrder = orders.map((order) => order.id);
    }
    this.updateState(false);
  };

  handleRemoveSelect = () => {
    this.selectedOrder = [];
    this.updateState(false);
  };

  wrapAPIRequest = async (
    requestFunc: () => Promise<void>,
    { successMsg = '', errorMsg = '' },
  ) => {
    this.updateState(true);
    try {
      await requestFunc();
      toast.success(successMsg);
    } catch (e) {
      if (errorMsg) toast.error(errorMsg);
      else if (e instanceof Error) toast.error(e.message);
    }
    this.updateState(false);
  };

  bulkActivateOrders = async () => {
    await this.wrapAPIRequest(
      async () => {
        await this.manager.activateOrders(this.selectedOrder);
        this.selectedOrder = [];
      },
      {
        successMsg: i18n.t<string>('orderList.labels.activated'),
        errorMsg: i18n.t<string>('orderList.labels.activationFailed'),
      },
    );
  };

  bulkDeactivateOrders = async () => {
    await this.wrapAPIRequest(
      async () => {
        await this.manager.deactivateOrders(this.selectedOrder);
        this.selectedOrder = [];
      },
      {
        successMsg: i18n.t<string>('orderList.labels.deactivated'),
        errorMsg: i18n.t<string>('orderList.labels.deactivationFailed'),
      },
    );
  };

  bulkApproveOrders = async () => {
    await this.wrapAPIRequest(
      async () => {
        await this.manager.approveOrders(this.selectedOrder);
        this.selectedOrder = [];
        await this.init();
      },
      {
        successMsg: i18n.t<string>('orderList.labels.approved'),
        errorMsg: i18n.t<string>('orderList.labels.approvalFailed'),
      },
    );
  };

  bulkRejectOrders = async () => {
    await this.wrapAPIRequest(
      async () => {
        await this.manager.rejectOrders(this.selectedOrder);
        this.selectedOrder = [];
        await this.init();
      },
      {
        successMsg: i18n.t<string>('orderList.labels.rejected'),
        errorMsg: i18n.t<string>('orderList.labels.rejectionFailed'),
      },
    );
  };

  get state() {
    return {
      ordersWithPagination: this.ordersWithPagination,
      loading: this.loading,
      redirectPath: this.redirectPath,
      selectedStateFilter: this.selectedStateFilter,
      selectedOrdersToShowInfo: this.selectedOrdersToShowInfo,
      selectedOrder: this.selectedOrder,
    };
  }

  get noDataDescription(): string {
    return 'orderList.headers.noDataAvailable';
  }

  get filterMenuTabConfigs(): FilterMenuTabConfig[] {
    return [
      {
        filterType: i18n.t<string>('orderList.labels.stateFilter'),
        menuTitle: i18n.t<string>('orderList.labels.stateFilterMenuTitle'),
        tag: i18n.t<string>('orderList.labels.stateTag'),
        selectedValues: this.selectedStateFilter,
        options: this.orderDeliveries,
        applyMethod: this.handleOnStateFilterApply,
      },
    ];
  }

  sortOrders = async (field, direction) => {
    const currentSortSetting = this.sortDescriptor[0];
    if (
      field === currentSortSetting.dataField &&
      direction === currentSortSetting.order
    ) {
      return;
    }
    this.handleSort(field, direction);
    await this.init();
  };

  onUnmount(handler) {
    this.event.remove(handler);
    this.redirectPath = undefined;
  }

  updateState(loading: boolean) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}
